import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DownloadIcon, Share2Icon, Cross2Icon, TrashIcon } from '@radix-ui/react-icons';
import InstagramShareTips from './InstagramShareTips';
import { useNotifications } from './Notifications';
import { isPreviewMode, previewImages } from '../devPreview';

const ImageHistory = ({ user, getAuthHeaders, backendUrl, refreshTrigger }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [instagramCaption, setInstagramCaption] = useState(null);
  const { notifyError, notifySuccess, notifyApiError } = useNotifications();

  const fetchImageHistory = useCallback(async () => {
    // Dev-only preview has no real token; show sample rows instead of an error.
    if (isPreviewMode()) {
      setImages(previewImages);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await axios.get(`${backendUrl}/api/images`, { headers });
      const fetchedImages = response.data.images || [];
      console.log('Fetched images:', fetchedImages);
      if (fetchedImages.length > 0) {
        console.log('First image timestamp:', fetchedImages[0].timestamp);
      }
      setImages(fetchedImages);
    } catch (error) {
      console.error('Error fetching image history:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, backendUrl]);

  // Fetch images when user logs in or when showing/hiding history
  useEffect(() => {
    if (user) {
      console.log('User effect triggered, fetching images...');
      fetchImageHistory();
    }
  }, [user, showHistory, fetchImageHistory]);

  // Refresh when refreshTrigger changes (new image generated)
  useEffect(() => {
    if (user && refreshTrigger > 0) {
      console.log('Refresh trigger activated:', refreshTrigger, 'Fetching images...');
      fetchImageHistory();
    }
  }, [refreshTrigger, user, fetchImageHistory]);

  const getTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    } else if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000);
    } else {
      return new Date(timestamp);
    }
  };

  const formatDate = (timestamp) => {
    const date = getTimestamp(timestamp);
    if (!date || isNaN(date.getTime())) {
      return 'Unknown';
    }
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getDateLabel = (timestamp) => {
    const date = getTimestamp(timestamp);
    if (!date || isNaN(date.getTime())) {
      return 'Unknown';
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
  };

  const getFilteredAndSortedImages = () => {
    let filtered = [...images];

    // Apply filter
    if (filterType !== 'all') {
      filtered = filtered.filter(img => img.featureType === filterType);
    }

    // Apply sort
    filtered.sort((a, b) => {
      const dateA = getTimestamp(a.timestamp);
      const dateB = getTimestamp(b.timestamp);
      if (!dateA || !dateB) return 0;
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  };

  const groupImagesByDate = (images) => {
    const groups = {};
    images.forEach(img => {
      const label = getDateLabel(img.timestamp);
      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(img);
    });
    return groups;
  };

  const getFeatureTypeBadge = (featureType) => {
    const badges = {
      'arrow-vision': { label: 'Arrow Vision', className: 'badge-arrow-vision' },
      'person-remover': { label: 'Person Remover', className: 'badge-person-remover' },
      'clothing-tryon': { label: 'Clothing Try-On', className: 'badge-clothing-tryon' }
    };
    return badges[featureType] || { label: 'Unknown', className: 'badge-unknown' };
  };

  const handleDeleteImage = async (imageId) => {
    setDeleting(true);
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${backendUrl}/api/images/${imageId}`, { headers });
      // Remove the image from local state
      setImages(prev => prev.filter(img => img.id !== imageId));
      setShowDeleteConfirm(null);
      // Close lightbox if open
      if (selectedImage && selectedImage.id === imageId) {
        setSelectedImage(null);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      notifyApiError(error, 'Could not delete that image. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const downloadImage = (imageData, filename = 'arrow-view.png') => {
    if (!imageData || !imageData.startsWith('data:image')) {
      notifyError('No image data available to download.');
      return;
    }

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadImageForSharing = async (imageData) => {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.post(`${backendUrl}/api/upload-for-sharing`,
        { imageData },
        { headers: authHeaders }
      );
      return response.data.publicUrl;
    } catch (error) {
      console.error('Error uploading image for sharing:', error);
      throw error;
    }
  };

  const shareToTwitter = async (imageData) => {
    try {
      const imageUrl = await uploadImageForSharing(imageData);
      const text = "Check out this AI-generated perspective view! What would you see from this arrow's point of view?";
      const hashtags = "AI,ArrowView,Perspective,GEMINI";

      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(imageUrl)}&hashtags=${hashtags}`;
      window.open(twitterUrl, '_blank', 'width=600,height=400');
    } catch (error) {
      console.error('Twitter sharing error:', error);
      notifyApiError(error, 'Could not prepare the image for Twitter. Please try again.');
    }
  };

  const shareToFacebook = async (imageData) => {
    try {
      const imageUrl = await uploadImageForSharing(imageData);
      const text = "Amazing AI-generated perspective view! See what the red arrow sees using Google Maps + GEMINI AI.";

      // Try Facebook Share Dialog API first
      if (window.FB && window.FB.ui) {
        window.FB.ui({
          method: 'share',
          href: imageUrl,
          quote: text
        });
      } else {
        // Fallback to Facebook sharer URL
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(imageUrl)}&t=${encodeURIComponent(text)}`;
        window.open(facebookUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      }
    } catch (error) {
      console.error('Facebook sharing error:', error);
      notifyApiError(error, 'Could not prepare the image for Facebook. Please try again.');
    }
  };

  const shareToInstagram = async (imageData) => {
    try {
      const text = "Check out this AI-generated perspective view! What would you see from this arrow's point of view? #AI #ArrowView #Perspective #GEMINI";

      // Check if Web Share API is available (mobile devices)
      if (navigator.share && navigator.canShare) {
        try {
          const imageUrl = await uploadImageForSharing(imageData);

          const shareData = {
            title: 'AI-Generated Arrow Vision',
            text: text,
            url: imageUrl
          };

          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        } catch (shareError) {
          console.log('Web Share API failed, falling back to download method:', shareError);
        }
      }

      // Fallback: Download image and copy caption
      downloadImage(imageData, `arrow-view-${Date.now()}.png`);

      let copied = false;
      try {
        await navigator.clipboard.writeText(text);
        copied = true;
      } catch (clipboardError) {
        copied = false;
      }

      setInstagramCaption({ text, copied });
      notifySuccess(copied
        ? 'Image downloaded and caption copied.'
        : 'Image downloaded. Copy the caption below for Instagram.');
    } catch (error) {
      console.error('Instagram sharing error:', error);
      notifyApiError(error, 'Could not prepare the Instagram share. Please try again.');
    }
  };

  if (!user) return null;

  const filteredImages = getFilteredAndSortedImages();
  const groupedImages = groupImagesByDate(filteredImages);

  return (
    <div className="image-history">
      <button
        className="btn btn-secondary"
        onClick={() => setShowHistory(!showHistory)}
      >
        {showHistory ? 'Hide' : 'Show'} Recent Images ({images.length})
      </button>

      {showHistory && (
        <div className="history-container">
          <h3>Your Recent Generations</h3>

          {/* Gallery Controls */}
          <div className="gallery-controls">
            <div className="gallery-control-group">
              <label htmlFor="filter-type">Filter:</label>
              <select
                id="filter-type"
                className="gallery-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="arrow-vision">Arrow Vision</option>
                <option value="person-remover">Person Remover</option>
                <option value="clothing-tryon">Clothing Try-On</option>
              </select>
            </div>
            <div className="gallery-control-group">
              <label htmlFor="sort-order">Sort:</label>
              <select
                id="sort-order"
                className="gallery-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading your images...</div>
          ) : filteredImages.length === 0 ? (
            <div className="no-images">
              <p>{images.length === 0 ? 'No images generated yet. Create your first image!' : 'No images match the selected filter.'}</p>
            </div>
          ) : (
            Object.entries(groupedImages).map(([dateLabel, dateImages]) => (
              <div key={dateLabel} className="image-date-group">
                <h4 className="date-group-header">{dateLabel}</h4>
                <div className="images-grid">
                  {dateImages.map((image) => (
                    <div key={image.id} className="image-item">
                      <div className="image-wrapper">
                        {image.imageData && image.imageData.startsWith('data:image') ? (
                          <>
                            <img
                              src={image.imageData}
                              alt="Generated view"
                              className="history-image"
                              loading="lazy"
                              onClick={() => setSelectedImage(image)}
                              style={{ cursor: 'pointer' }}
                            />
                            <div className="image-actions-overlay">
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => downloadImage(image.imageData, `${image.featureType || 'image'}-${image.id}.png`)}
                                title="Download image"
                              >
                                <DownloadIcon />
                              </button>

                              <div className="share-dropdown" style={{ display: 'inline-block', position: 'relative' }}>
                                <button
                                  className="btn btn-sm btn-share share-toggle"
                                  title="Share image"
                                  onClick={(e) => {
                                    const dropdown = e.target.closest('.share-dropdown').querySelector('.share-options');
                                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                  }}
                                >
                                  <Share2Icon />
                                </button>

                                <div className="share-options" style={{
                                  display: 'none',
                                  position: 'absolute',
                                  top: '100%',
                                  right: '0',
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(20px)',
                                  WebkitBackdropFilter: 'blur(20px)',
                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                  borderRadius: '12px',
                                  padding: '8px',
                                  zIndex: 1000,
                                  minWidth: '140px',
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                }}>
                                  <button
                                    className="btn btn-sm share-platform-btn"
                                    onClick={() => shareToTwitter(image.imageData)}
                                    style={{ display: 'block', width: '100%', marginBottom: '4px', backgroundColor: '#000000', color: 'white', borderRadius: '8px' }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
                                      <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                    Twitter
                                  </button>

                                  <button
                                    className="btn btn-sm share-platform-btn"
                                    onClick={() => shareToFacebook(image.imageData)}
                                    style={{ display: 'block', width: '100%', marginBottom: '4px', backgroundColor: '#1877F2', color: 'white', borderRadius: '8px' }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
                                      <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                    Facebook
                                  </button>

                                  <button
                                    className="btn btn-sm share-platform-btn"
                                    onClick={() => shareToInstagram(image.imageData)}
                                    style={{ display: 'block', width: '100%', backgroundColor: '#E4405F', color: 'white', borderRadius: '8px' }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
                                      <path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                    </svg>
                                    Instagram
                                  </button>
                                </div>
                              </div>

                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => setShowDeleteConfirm(image.id)}
                                title="Delete image"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </>
                        ) : (
                          // New images without stored data (metadata only)
                          <div className="placeholder-image">
                            <div className="placeholder-content">
                              <span className="image-icon">Image Generated</span>
                              {(image.originalImageSize || image.imageSize) && (
                                <p className="image-size">
                                  {((image.originalImageSize || image.imageSize) / 1024 / 1024).toFixed(1)}MB
                                  {image.compressedSize && (
                                    <span style={{ color: '#666', fontSize: '10px' }}>
                                      {' '} to {(image.compressedSize / 1024).toFixed(0)}KB
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="image-overlay">
                          <div className="image-info">
                            <span className={`feature-type-badge ${getFeatureTypeBadge(image.featureType).className}`}>
                              {getFeatureTypeBadge(image.featureType).label}
                            </span>
                            <p className="image-prompt">{image.prompt}</p>
                            <p className="image-date">{formatDate(image.timestamp)}</p>
                            {image.location && (
                              <p className="image-location">
                                {image.location.lat.toFixed(4)}, {image.location.lng.toFixed(4)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {instagramCaption && (
        <InstagramShareTips
          caption={instagramCaption.text}
          copied={instagramCaption.copied}
          onClose={() => setInstagramCaption(null)}
        />
      )}

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>
              <Cross2Icon width={24} height={24} />
            </button>
            <img
              src={selectedImage.imageData}
              alt="Generated view"
              className="lightbox-image"
            />
            <div className="lightbox-info">
              <span className={`feature-type-badge ${getFeatureTypeBadge(selectedImage.featureType).className}`}>
                {getFeatureTypeBadge(selectedImage.featureType).label}
              </span>
              <p className="lightbox-prompt">{selectedImage.prompt}</p>
              <p className="lightbox-date">{formatDate(selectedImage.timestamp)}</p>
              {selectedImage.location && (
                <p className="lightbox-location">
                  Location: {selectedImage.location.lat.toFixed(4)}, {selectedImage.location.lng.toFixed(4)}
                </p>
              )}
            </div>
            <div className="lightbox-actions">
              <button
                className="btn btn-secondary"
                onClick={() => downloadImage(selectedImage.imageData, `${selectedImage.featureType || 'image'}-${selectedImage.id}.png`)}
              >
                <DownloadIcon style={{ marginRight: '8px' }} />
                Download
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteConfirm(selectedImage.id)}
              >
                <TrashIcon style={{ marginRight: '8px' }} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="delete-confirm-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Image?</h3>
            <p>Are you sure you want to delete this image? This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteImage(showDeleteConfirm)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageHistory;
